<template>
  <modal ref="modal" @close="onClose" class="modal">
    <template slot="title">テストメール送信</template>
    <template slot="name">メールアドレス</template>
    <template slot="input">
      <tag-like-email-input ref="tagLikeEmailInput"/>
      <p class="notice">
        最大{{ maxEmailCount }}件まで指定できます (<span
          class="count"
          :class="{'red': emails.length > maxEmailCount}"
        >{{ emails.length }}/{{ maxEmailCount }}</span>)
      </p>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <p class="preset-label">登録済みの送信先を選択</p>
      <div class="common-select">
        <select v-model="selectedTestEmailPresetId" @input="onSelectTestEmailPreset($event.target.value)">
          <option value="">---</option>
          <option
            v-for="testEmailPreset in testEmailPresets"
            :value="testEmailPreset.id"
            :key="testEmailPreset.id"
          >
            {{ testEmailPreset.name }}
          </option>
        </select>
      </div>
    </template>
    <template slot="submit">
      <button v-if="!isLoading" type="button" @click="onSubmit" class="submit normal-btn">送信</button>
      <button v-else type="button" disabled class="submit normal-btn">送信</button>
    </template>
  </modal>
</template>

<script>
import base from '@/components/Content/Check/ContentCheckBase'
import TagLikeEmailInput from '@/components/Util/TagLikeEmailInput'
import modal from '@/components/Util/Modal'
import constants from '@/constants'

const MAX_EMAIL_COUNT = 10

export default {
  name: 'send-test-mail',
  mixins: [ base ],
  props: [
    'contentId',
  ],
  data () {
    return {
      rawHTML: '',
      mobileCSS: '',
      isLoading: false,
      maxEmailCount: MAX_EMAIL_COUNT,
      errorMessage: '',
      testEmailPresets: [],
      selectedTestEmailPresetId: '',
      isReplacingTestEmailPreset: false,
    }
  },
  components: {
    modal,
    TagLikeEmailInput,
  },
  async mounted () {
    this.isLoading = true
    this.$store.commit('clearTagLikeEmailInput')
    const sortJson = await this.$http({
      url: '/user/mphe_settings/default_display_test_email_preset',
      type: 'GET',
    })
    if (!sortJson || sortJson.status !== 200) {
      this.isLoading = false
      return
    }
    const { sortTarget, sortType } = sortJson.data.display
    const json = await this.$http({
      type: 'GET',
      url: `/test_email_preset/?sortTarget=${sortTarget}&sortType=${sortType}`,
    })
    if (json && json.status === 200) {
      this.testEmailPresets = json.data.testEmailPresets.map(testEmailPreset => ({
        id: testEmailPreset.id,
        name: this.$utils.string.b64Decode(testEmailPreset.name),
        preset: testEmailPreset.preset.split(','),
      }))
    }
    this.isLoading = false
  },
  methods: {
    onClose () {
      this.$emit('close')
    },
    onSelectTestEmailPreset (id) {
      if (!id) return
      this.isReplacingTestEmailPreset = true
      this.selectedTestEmailPresetId = id
      const preset = this.testEmailPresets.find(testEmailPreset => testEmailPreset.id === Number(id)).preset
      this.$refs.tagLikeEmailInput.replaceEmails(preset)
      this.$nextTick(() => {
        this.isReplacingTestEmailPreset = false
      })
    },
    validate () {
      if (this.emails.some(tag => tag.text.length > constants.maxEmailLength)) {
        this.errorMessage = 'テスト送信先は最大' + constants.maxEmailLength + '字までです'
        return false
      } else if (this.emails.length > constants.maxTagLikeEmailCount) {
        this.errorMessage = 'テスト送信先は最大' + constants.maxTagLikeEmailCount + '件までです'
        return false
      } else if (this.emails.length === 0) {
        this.errorMessage = 'テスト送信先が入力されていません'
        return false
      } else if (!this.$store.getters.isValidTagLikeEmailInput) {
        this.errorMessage = '無効なテスト送信先が入力されています'
        return false
      }
      return true
    },
    async onSubmit () {
      await new Promise(resolve => setTimeout(resolve, 50)) // store が更新されるまで待つ
      if (!this.validate()) return
      this.isLoading = true
      // 送信前に、HTMLを作成してサイズを取得し、保存リクエストを送る
      this.mobileCSS = this.createMobileCSS()
      this.createRawHTML()
      await this.getContentSize()
      const saveJson = await this.$store.dispatch('savePreviewData')
      if (!saveJson || saveJson.status !== 200) return

      // コードパーツ 未使用の画像を削除
      this.$store.commit('deleteCodePartUnUseImage', this.$store.getters.getSectionAll)

      const sections = await this.getTestPublicSections({ type: 'testmail' })
      if (!sections) return

      this.createRawHTML(sections)

      // 送信するHTMLメール
      const formData = new FormData()
      formData.append('contentId', this.contentId || this.$route.params.id)
      formData.append('body', this.rawHTML)
      // title
      formData.append('title', this.$store.getters.getContentInfo.name)
      // 送信メールアドレス
      const emails = this.emails.map(email => email.text).join(',')
      formData.append('email', emails)
      // モバイルスタイルCSS
      if (this.mobileCSS) formData.append('mobileCss', this.mobileCSS)
      // メール用CSS
      formData.append('headCss', this.createHeadCSS())
      // outlook CSS
      formData.append('outlookCss', this.createOutlookCSS())
      // エラーポップアップを抑制
      formData.append('formValidationKeys[]', 'email')
      const json = await this.$http({
        type: 'POST',
        url: '/test_mail',
        params: formData,
      })
      if (json && json.status === 200) {
        this.$emit('close')
      } else {
        this.errorMessage = json.validations.email
      }
      this.isLoading = false
    },
  },
  computed: {
    emails () {
      return this.$store.state.tagLikeEmailInput.emails
    }
  },
  watch: {
    'emails' (newVal) {
      // プリセット選択後にメールアドレス変更があった場合、選択を解除する。ただしプリセット変更による変更は除外
      if (this.selectedTestEmailPresetId && !this.isReplacingTestEmailPreset) {
        this.selectedTestEmailPresetId = ''
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.error-message {
  display: inline-block;
  line-height: 2;
  max-width: 410px;
}
.submit {
  font-size: 14px;
  border-radius: 5px;
  color: #fff;
  border: solid 1px #484848;
  background: #484848;
  &:hover {
    background: #484848;
    opacity: 0.8;
  }
  &:disabled {
    border: solid 1px #6c757d;
    background: #6c757d;
    opacity: 0.8;
    cursor: default;
  }
}
.notice {
  font-size: 14px;
  margin-top: 5px;
  .count {
    color: #000;
    &.red {
      color: #ff0000;
    }
  }
}
.preset-label {
  margin-top: 15px;
}
.common-select {
  border: 1px solid #ccc;
  border-radius: 2px;
  select {
    width: 400px;
    // border のスタイルは tag-like-email-input と合わせる
  }
}
</style>

<style lang="scss">
.modal {
  #modal-content {
    width: 600px;
  }
}
</style>
